export type Section = 'profile' | 'lifestyle' | 'motivation' | 'plan'

export type PrimaryGoal =
  | 'manage_ckd'
  | 'slow_progression'
  | 'dialysis_support'
  | 'post_transplant'
  | 'kidney_stones'
  | 'caregiver'
  | 'general_health'
  | 'exploring'

export type KidneyStatus =
  | 'stage_1'
  | 'stage_2'
  | 'stage_3a'
  | 'stage_3b'
  | 'stage_4'
  | 'stage_5'
  | 'on_dialysis'
  | 'kidney_stones'
  | 'no_diagnosis'
  | 'not_sure'

export type DiagnosisTimeline =
  | 'just_now'
  | 'under_6mo'
  | '6mo_to_2yr'
  | 'over_2yr'
  | 'prevention'

export type Comorbidity =
  | 'diabetes'
  | 'high_blood_pressure'
  | 'heart_disease'
  | 'gout'
  | 'anemia'
  | 'bone_disease'
  | 'none'

export type DialysisType =
  | 'hemodialysis'
  | 'peritoneal'
  | 'home_hemo'
  | 'not_started'

export type DietKnowledge = 'beginner' | 'some' | 'intermediate' | 'expert'

export type Struggle =
  | 'reading_labels'
  | 'hidden_phosphorus'
  | 'eating_out'
  | 'portion_sizes'
  | 'potassium_foods'
  | 'meal_ideas'
  | 'sodium_limits'
  | 'fluid_tracking'
  | 'consistency'

export type EatingStyle = 'home' | 'mixed' | 'eating_out' | 'meal_prep' | 'caregiver'

export type MotivationSource = 'health' | 'family' | 'labs' | 'avoid_dialysis' | 'energy' | 'control'

export type CommitmentLevel = 'all_in' | 'pretty' | 'trying' | 'exploring'

export type FeaturePriority =
  | 'meal_scanning'
  | 'recipes'
  | 'tracking'
  | 'restaurant_guide'
  | 'education'
  | 'fluid_tracking'

export interface OnboardingData {
  // Profile
  primaryGoal: PrimaryGoal | null
  kidneyStatus: KidneyStatus | null
  diagnosisTimeline: DiagnosisTimeline | null
  comorbidities: Comorbidity[]
  weightKg: number | null
  /** The unit picked on the weight step (4.39 F6); null when skipped. */
  weightUnit: 'kg' | 'lbs' | null
  dialysisType: DialysisType | null

  // Lifestyle
  dietKnowledge: DietKnowledge | null
  struggles: Struggle[]
  eatingStyle: EatingStyle | null
  motivationSource: MotivationSource | null
  commitmentLevel: CommitmentLevel | null
  featurePriority: FeaturePriority | null

  // Plan
  email: string | null
  marketingConsent: boolean

  // Payment
  paddleTransactionId: string | null
  /**
   * Paid earlier but no account yet (the email check found a parked web purchase):
   * the offer is skipped and the account step finishes setup + claims the purchase.
   * Absent in sessions persisted before 2026-10-24 — read it as false.
   */
  paidSetup?: boolean
  /** Set when a parked purchase was claimed onto the new account (Success says Pro is active). */
  webPurchaseClaimed?: boolean
  /** How the account step signed in, and the account's email — Success shows matching app steps. */
  authMethod?: 'email' | 'google' | null
  accountEmail?: string | null
  purchasePlanId: string | null
  purchasePriceUsd: number | null

  // Meta CAPI dedup — generated browser-side on Purchase, replayed server-side
  metaPurchaseEventId: string | null

  // Funnel variant captured at load time — persisted to Firestore so server-side
  // CAPI fires (paddleWebhook + paddleSubscriptionWebhook) can attribute renewals
  // and refunds to the same variant the buyer originally went through.
  funnelVariant: string | null
}

export interface OnboardingStep {
  key: string
  section: Section
}

export const initialOnboardingData: OnboardingData = {
  primaryGoal: null,
  kidneyStatus: null,
  diagnosisTimeline: null,
  comorbidities: [],
  weightKg: null,
  weightUnit: null,
  dialysisType: null,

  dietKnowledge: null,
  struggles: [],
  eatingStyle: null,
  motivationSource: null,
  commitmentLevel: null,
  featurePriority: null,

  email: null,
  marketingConsent: false,

  paddleTransactionId: null,
  purchasePlanId: null,
  purchasePriceUsd: null,
  metaPurchaseEventId: null,
  funnelVariant: null,
}
